import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { FaExclamationTriangle } from "react-icons/fa";
import { FiCreditCard, FiFileText, FiTrash2, FiX } from "react-icons/fi";
import Loader from "./Loader";
import { baseurl } from "../config/url";
import { showErrorToast, showSuccessToast } from "../utils/toaster";
import {
  calculateVisibilityCharges,
  calculateDepositCharges,
} from "../utils/calculateVisibilityCharges";
import generateInvoice from "../utils/generateinvoice";

const { displayRazorpay } = require("../utils/rozorpay");

/**
 * ✅ FIX: baseurl can be:
 *   - https://api.ogcs.co.in
 *   - https://api.ogcs.co.in/api
 * Your code was doing `${baseurl}/api/...` always, which can produce /api/api/... in prod.
 */
const normalizeBase = (url) => String(url || "").trim().replace(/\/+$/, "");

const buildApiUrl = (path) => {
  const b = normalizeBase(baseurl);
  const p = path.startsWith("/") ? path : `/${path}`;

  // If base already ends with /api, and path starts with /api -> remove one /api
  if (b.endsWith("/api") && p.startsWith("/api/")) return `${b}${p.replace("/api", "")}`;

  // If base does NOT end with /api, but path starts with /api -> normal
  return `${b}${p}`;
};

const safeNumber = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const safeVisibilityLevel = (v) => {
  const s = String(v || "1X").toUpperCase().trim();
  return ["1X", "2X", "3X", "4X"].includes(s) ? s : "1X";
};

const SavedProducts = () => {
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState("Sale By OGCS");
  const [sellerDetails, setSellerDetails] = useState(null);
  const [otherPaymentDetails, setOtherPaymentDetails] = useState({
    method: "",
    amount: "",
    date: "",
    bankName: "",
    transactionId: "",
    ifscCode: "",
    accountNumber: "",
    productId: "",
  });

  const getToken = () => localStorage.getItem("token");
  const getSellerId = () => localStorage.getItem("sellerId");

  const axiosAuth = useMemo(() => {
    const token = getToken();
    return {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      // ✅ helpful in some prod setups (cookies/cors); won’t break if unused
      withCredentials: true,
    };
  }, []);

  const fetchSavedProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        buildApiUrl("/api/seller/incomplete-payment-products"),
        axiosAuth
      );
      setProducts(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error("Error fetching saved products:", error?.response?.data || error?.message);
      showErrorToast("Failed to load saved products (API error).");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [axiosAuth]);

  const fetchSellerDetails = useCallback(async () => {
    try {
      const sellerId = getSellerId();
      if (!sellerId) {
        showErrorToast("Seller ID not found.");
        return;
      }

      // ✅ FIX: In production this route often needs token
      const res = await axios.get(
        buildApiUrl(`/api/seller/seller-by-id/${sellerId}`),
        axiosAuth
      );
      setSellerDetails(res?.data || {});
    } catch (error) {
      console.error("Error fetching seller details:", error?.response?.data || error?.message);
      showErrorToast("Error fetching seller details");
      setSellerDetails(null);
    }
  }, [axiosAuth]);

  useEffect(() => {
    fetchSavedProducts();
    fetchSellerDetails();
  }, [fetchSavedProducts, fetchSellerDetails]);

  const handleProductSelect = (productId, saleType) => {
    // keep rule: cannot mix OGCS + Seller
    if (
      saleType === "Sale By Seller" &&
      selectedProducts.some((id) => {
        const p = products.find((x) => x._id === id);
        return p && p.saleType === "Sale By OGCS";
      })
    ) {
      showErrorToast("You cannot select both 'Sale By Seller' and 'Sale By OGCS' products.");
      return;
    }

    if (
      saleType === "Sale By OGCS" &&
      selectedProducts.some((id) => {
        const p = products.find((x) => x._id === id);
        return p && p.saleType === "Sale By Seller";
      })
    ) {
      showErrorToast("You cannot select both 'Sale By Seller' and 'Sale By OGCS' products.");
      return;
    }

    setSelectedProducts((prev) =>
      prev.includes(productId) ? prev.filter((id) => id !== productId) : [...prev, productId]
    );

    setOtherPaymentDetails((prev) => ({ ...prev, productId }));
  };

  const removeProduct = (productId) => {
    setSelectedProducts((prev) => prev.filter((id) => id !== productId));
  };

  const filteredProducts = useMemo(
    () => products.filter((p) => p.saleType === activeTab),
    [products, activeTab]
  );

  const calculateHireVisibilityCharges = (monthlyHireCharges, visibilityLevel) => {
    const tiers = {
      "1X": { rate: 0.1, min: 15000, max: 90000 },
      "2X": { rate: 0.11, min: 19500, max: 99000 },
      "3X": { rate: 0.12, min: 25350, max: 108900 },
      "4X": { rate: 0.13, min: 32955, max: 119790 },
    };

    const level = safeVisibilityLevel(visibilityLevel);
    const { rate, min, max } = tiers[level];
    const charges = safeNumber(monthlyHireCharges) * rate;
    return Math.max(min, Math.min(charges, max));
  };

  /**
   * ✅ FIX: Never throw from calculations (prod data may have missing fields)
   * This prevents your "Category, subcategory, price, and quantity are required" crash.
   */
  const calculateTotalPrice = (product) => {
    const GST_RATE = safeNumber(product?.gstnumber, 18);

    const price = safeNumber(product?.price);
    const qty = safeNumber(product?.maxquantity);

    let visibilityPrice = 0;
    let visibilityHirePrice = 0;
    let powerPackPrice = 0;
    let depositCharges = 0;

    const saleType = product?.saleType;
    const saleProduct = product?.saleProduct;

    // Visibility (Seller)
    if (saleType === "Sale By Seller") {
      try {
        visibilityPrice = calculateVisibilityCharges(
          price,
          qty,
          safeVisibilityLevel(product?.visibilityLevel)
        );
      } catch (e) {
        // ✅ don’t crash UI
        visibilityPrice = 0;
      }
    }

    // Hire visibility
    if (saleProduct === "Hire") {
      visibilityHirePrice = calculateHireVisibilityCharges(
        safeNumber(product?.hireCharges?.perMonth),
        safeVisibilityLevel(product?.visibilityLevel)
      );
    }

    // Deposit (OGCS, not Hire)
    if (saleType === "Sale By OGCS" && saleProduct !== "Hire") {
      const category =
        typeof product?.category === "object" ? product?.category?.name : product?.category;
      const subcategory =
        typeof product?.subcategory === "object"
          ? product?.subcategory?.name
          : product?.subcategory;

      if (category && subcategory && price > 0 && qty > 0) {
        try {
          depositCharges = calculateDepositCharges(category, subcategory, price, qty);
        } catch (e) {
          depositCharges = 0;
        }
      }
    }

    if (product?.powerPackVisibility) powerPackPrice = 25000;

    // GST
    const hireVisibilityGST = (visibilityHirePrice * GST_RATE) / 100;
    const visibilityGST = (visibilityPrice * GST_RATE) / 100;
    const powerPackGST = (powerPackPrice * GST_RATE) / 100;

    if (saleType === "Sale By OGCS") {
      return depositCharges + powerPackPrice + powerPackGST;
    }

    if (saleType === "Sale By Seller") {
      return visibilityPrice + visibilityGST + powerPackPrice + powerPackGST;
    }

    if (saleProduct === "Hire") {
      return visibilityHirePrice + hireVisibilityGST + powerPackPrice + powerPackGST;
    }

    return 0;
  };

  const calculateSelectedTotalPrice = (ids) => {
    if (!Array.isArray(ids) || ids.length === 0) return 0;

    const sorted = ids
      .map((id) => products.find((p) => p._id === id))
      .filter(Boolean)
      .sort((a, b) => safeNumber(b.price) * safeNumber(b.maxquantity) - safeNumber(a.price) * safeNumber(a.maxquantity));

    let combinedVisibilityCharges = 0;
    let totalDepositCharges = 0;

    // Simple safe total (your grouping logic can stay; but safest is: sum each product price)
    // If you MUST keep grouping logic, keep it, but ensure it never throws.
    // Here: keep your intent—sum visibility for seller, sum deposit for OGCS.
    for (const p of sorted) {
      const saleType = p?.saleType;
      const saleProduct = p?.saleProduct;

      if (saleType === "Sale By Seller") {
        try {
          combinedVisibilityCharges += calculateVisibilityCharges(
            safeNumber(p?.price),
            safeNumber(p?.maxquantity),
            safeVisibilityLevel(p?.visibilityLevel)
          );
        } catch {
          combinedVisibilityCharges += 0;
        }
      }

      if (saleType === "Sale By OGCS" && saleProduct !== "Hire") {
        const category = typeof p?.category === "object" ? p?.category?.name : p?.category;
        const subcategory =
          typeof p?.subcategory === "object" ? p?.subcategory?.name : p?.subcategory;

        if (category && subcategory) {
          try {
            totalDepositCharges += calculateDepositCharges(
              category,
              subcategory,
              safeNumber(p?.price),
              safeNumber(p?.maxquantity)
            );
          } catch {
            totalDepositCharges += 0;
          }
        }
      }
    }

    const GST_RATE = 18;
    const visibilityGST = (combinedVisibilityCharges * GST_RATE) / 100;

    const total = combinedVisibilityCharges + visibilityGST + totalDepositCharges;
    return safeNumber(total, 0);
  };

  const handleOtherPaymentSubmit = async () => {
    const { method, amount, date, bankName, transactionId, ifscCode, accountNumber } =
      otherPaymentDetails;

    if (
      !method ||
      !amount ||
      !date ||
      !bankName ||
      !transactionId ||
      !ifscCode ||
      !accountNumber ||
      selectedProducts.length === 0
    ) {
      showErrorToast("Please fill all the required fields for offline payment.");
      return;
    }

    try {
      const sellerId = getSellerId();

      await axios.post(
        buildApiUrl("/api/payment/other-payment"),
        {
          method,
          amount,
          date,
          bankName,
          transactionId,
          ifscCode,
          accountNumber,
          productIds: selectedProducts,
          sellerId,
        },
        axiosAuth
      );

      showSuccessToast("Offline payment recorded successfully!");
      setOtherPaymentDetails({
        method: "",
        amount: "",
        date: "",
        bankName: "",
        transactionId: "",
        ifscCode: "",
        accountNumber: "",
        productId: "",
      });
      setShowModal(false);
      fetchSavedProducts();
    } catch (error) {
      console.error("Error storing offline payment:", error?.response?.data || error?.message);
      showErrorToast("Failed to record offline payment. Please try again.");
    }
  };

  const handlePaymentSuccess = async (paymentData) => {
    try {
      const sellerId = getSellerId();

      const selectedProductDetails = selectedProducts
        .map((productId) => {
          const product = products.find((p) => p._id === productId);
          if (!product) return null;

          const gstRate = safeNumber(product?.gstnumber, 18);
          const saleType = product?.saleType;
          const saleProduct = product?.saleProduct;

          let totalProductAmount = calculateTotalPrice(product);
          let visibilityAmount = 0;
          let hireVisibilityCharge = 0;

          if (saleProduct === "Hire") {
            hireVisibilityCharge = calculateHireVisibilityCharges(
              safeNumber(product?.hireCharges?.perMonth),
              safeVisibilityLevel(product?.visibilityLevel)
            );
            totalProductAmount = hireVisibilityCharge;
          } else if (saleType === "Sale By Seller") {
            try {
              visibilityAmount = calculateVisibilityCharges(
                safeNumber(product?.price),
                safeNumber(product?.maxquantity),
                safeVisibilityLevel(product?.visibilityLevel)
              );
            } catch {
              visibilityAmount = 0;
            }
          }

          return {
            productId: product._id,
            totalProductAmount: safeNumber(totalProductAmount, 0),
            visibilityAmount: safeNumber(visibilityAmount, 0),
            hireVisibilityCharge: safeNumber(hireVisibilityCharge, 0),
            saleType,
            gst: (safeNumber(totalProductAmount, 0) * gstRate) / 100,
            visibilityLevel: safeVisibilityLevel(product?.visibilityLevel),
            powerPackAmount: product?.powerPackVisibility ? 25000 : 0,
          };
        })
        .filter(Boolean);

      const totalAmount = selectedProductDetails.reduce(
        (acc, p) => acc + safeNumber(p.totalProductAmount),
        0
      );

      const payload = {
        sellerId,
        products: selectedProductDetails,
        totalAmount,
        orderCreationId: paymentData.razorpayOrderId,
        razorpayOrderId: paymentData.razorpayOrderId,
        razorpayPaymentId: paymentData.razorpayPaymentId,
        razorpaySignature: paymentData.razorpaySignature,
      };

      const isAllOGCS = selectedProductDetails.every((p) => p.saleType === "Sale By OGCS");
      const isAllSeller = selectedProductDetails.every((p) => p.saleType === "Sale By Seller");

      if (isAllOGCS) {
        await axios.post(buildApiUrl("/api/payment/store-deposit-payment"), payload, axiosAuth);
      } else if (isAllSeller) {
        await axios.post(
          buildApiUrl("/api/payment/seller-visibility-payment-store-todb"),
          payload,
          axiosAuth
        );
      } else {
        showErrorToast("Mixed product types not supported for one payment.");
        return;
      }

      showSuccessToast("Payment stored successfully!");
      fetchSavedProducts();
    } catch (error) {
      console.error("Error storing payment:", error?.response?.data || error?.message);
      showErrorToast("Failed to store payment details. Please try again.");
    }
  };

  const handlePayForSelected = () => {
    if (selectedProducts.length === 0) {
      showErrorToast("Please select at least one product to proceed with the payment.");
      return;
    }

    const totalPrice = calculateSelectedTotalPrice(selectedProducts);

    if (totalPrice <= 0) {
      showErrorToast("Total price is invalid. Please review your selection.");
      return;
    }

    if (paymentMethod === "Razorpay") {
      const amountInPaise = totalPrice;
      displayRazorpay(amountInPaise, (paymentData) => handlePaymentSuccess(paymentData));
    } else if (paymentMethod === "Offline") {
      setShowModal(true);
    } else {
      showErrorToast("Please select a payment method.");
    }
  };

  const handleGenerateInvoice = () => {
    if (selectedProducts.length === 0) {
      showErrorToast("Please select at least one product for invoice.");
      return;
    }

    if (!sellerDetails) {
      showErrorToast("Seller details not found.");
      return;
    }

    const selectedProductDetails = selectedProducts
      .map((productId) => {
        const product = products.find((p) => p._id === productId);
        if (!product) return null;

        return {
          productId: product._id,
          productName: product.name || "Unknown Product",
          productPrice: safeNumber(product.price),
          maxquantity: safeNumber(product.maxquantity),
          saleType: product.saleType || "Unknown",
          visibilityLevel: safeVisibilityLevel(product.visibilityLevel),
          powerPackPrice: safeNumber(product.savedPowerPackPrice),
          depositAmount: safeNumber(product.savedDepositAmount),
          gstAmount: safeNumber(product.savedGstAmount),
          totalAmount: safeNumber(product.savedTotalAmount),
        };
      })
      .filter(Boolean);

    const invoiceId = `INV-${Date.now()}`;
    const totalAmount = calculateSelectedTotalPrice(selectedProducts);

    try {
      generateInvoice(sellerDetails, selectedProductDetails, invoiceId, totalAmount);
      showSuccessToast("Invoice downloaded successfully!");
    } catch (error) {
      console.error("Invoice error:", error?.message);
      showErrorToast("Failed to download invoice.");
    }
  };

  const selectedTotal = useMemo(
    () => calculateSelectedTotalPrice(selectedProducts),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedProducts, products, activeTab]
  );

  const selectedCount = selectedProducts.length;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top header */}
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900">
              Saved Products
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Complete pending payments to activate listings.
            </p>
          </div>

          <button
            onClick={handleGenerateInvoice}
            className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white font-semibold hover:bg-emerald-700 shadow-sm"
          >
            <FiFileText />
            Generate Invoice
          </button>
        </div>

        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pb-4">
          <div className="flex flex-wrap gap-2">
            {["Sale By Seller", "Sale By OGCS", "Sale By BestPrice"].map((tab) => {
              const active = activeTab === tab;
              return (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                    active
                      ? "bg-blue-600 text-white shadow"
                      : "bg-white text-slate-700 border hover:bg-slate-50"
                  }`}
                >
                  {tab}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {loading ? (
          <div className="py-16">
            <Loader />
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="bg-white border rounded-2xl p-8 text-center">
            <p className="text-slate-600 font-medium">
              No products with incomplete payments found for{" "}
              <span className="font-bold text-slate-900">{activeTab}</span>.
            </p>
          </div>
        ) : (
          <>
            {/* Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filteredProducts.map((product) => {
                const selected = selectedProducts.includes(product._id);
                const total = calculateTotalPrice(product);

                return (
                  <div
                    key={product._id}
                    className={`group bg-white border rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all ${
                      selected ? "ring-2 ring-blue-500" : ""
                    }`}
                  >
                    {/* Image */}
                    <div className="relative h-44 w-full bg-slate-100">
                      {product?.image?.data ? (
                        <img
                          src={`data:${product?.image?.contentType};base64,${product?.image?.data}`}
                          alt={product.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-500">
                          No Image
                        </div>
                      )}

                      {/* Pending badge */}
                      <div className="absolute top-3 left-3 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-50 text-red-700 border border-red-100 text-xs font-bold">
                        <FaExclamationTriangle />
                        Payment Pending
                      </div>

                      {/* Select checkbox */}
                      <label className="absolute top-3 right-3">
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => handleProductSelect(product._id, product.saleType)}
                          className="h-5 w-5 accent-blue-600 cursor-pointer"
                        />
                      </label>
                    </div>

                    {/* Body */}
                    <div className="p-4">
                      <h3 className="text-base font-extrabold text-slate-900 truncate">
                        {product.name}
                      </h3>

                      <div className="mt-2 space-y-1">
                        {product.priceType === "Ask for Best Price" ? (
                          <p className="text-sm font-semibold text-orange-600">
                            Ask for Best Price
                          </p>
                        ) : (
                          <p className="text-sm text-slate-600">
                            Price per Unit:{" "}
                            <span className="font-bold text-slate-900">
                              ₹{safeNumber(product.price)}
                            </span>
                          </p>
                        )}

                        {product.saleProduct === "Hire" ? (
                          <p className="text-sm text-slate-600">
                            Hire Price:{" "}
                            <span className="font-bold text-slate-900">
                              ₹
                              {calculateHireVisibilityCharges(
                                product.hireCharges?.perMonth || 0,
                                product.visibilityLevel || "1X"
                              ).toFixed(2)}
                            </span>
                          </p>
                        ) : product.priceType === "Ask for Best Price" ? (
                          <p className="text-sm text-orange-600">Price will be discussed.</p>
                        ) : (
                          <p className="text-sm text-slate-600">
                            Total Price:{" "}
                            <span className="font-bold text-slate-900">
                              ₹{Number(total || 0).toFixed(2)}
                            </span>
                          </p>
                        )}
                      </div>

                      {/* Actions row */}
                      <div className="mt-4 flex items-center justify-between">
                        <button
                          onClick={() => removeProduct(product._id)}
                          className="inline-flex items-center gap-2 text-sm font-semibold text-red-600 hover:text-red-700"
                        >
                          <FiTrash2 />
                          Remove
                        </button>

                        <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-slate-100 text-slate-700">
                          {product.saleType || "-"}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Sticky Summary + Pay */}
            <div className="mt-6 bg-white border rounded-2xl p-4 sm:p-5 shadow-sm flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <p className="text-sm text-slate-500">Selected</p>
                <p className="text-lg font-extrabold text-slate-900">
                  {selectedCount} product{selectedCount === 1 ? "" : "s"}
                </p>
                <p className="text-sm text-slate-600 mt-1">
                  Total:{" "}
                  <span className="text-slate-900 font-extrabold">
                    ₹{Number(selectedTotal || 0).toFixed(2)}
                  </span>
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                <select
                  value={paymentMethod}
                  onChange={(e) => {
                    setPaymentMethod(e.target.value);
                    if (e.target.value === "Offline") setShowModal(true);
                  }}
                  className="h-11 px-3 border rounded-xl bg-white text-slate-900 w-full sm:w-64 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Payment Method</option>
                  <option value="Razorpay">Razorpay</option>
                  <option value="Offline">Offline Payment</option>
                </select>

                <button
                  onClick={handlePayForSelected}
                  className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-xl bg-blue-600 text-white font-extrabold hover:bg-blue-700 transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!selectedCount}
                >
                  <FiCreditCard />
                  Pay Now
                </button>
              </div>
            </div>

            {/* Mobile Invoice Button */}
            <button
              onClick={handleGenerateInvoice}
              className="sm:hidden mt-4 w-full inline-flex items-center justify-center gap-2 h-11 rounded-xl bg-emerald-600 text-white font-extrabold hover:bg-emerald-700"
            >
              <FiFileText />
              Generate Invoice
            </button>
          </>
        )}
      </div>

      {/* OFFLINE PAYMENT MODAL */}
      {showModal && (
        <div className="fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowModal(false)} />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="w-full max-w-5xl bg-white rounded-2xl shadow-2xl overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 border-b bg-slate-50">
                <div>
                  <h3 className="text-lg font-extrabold text-slate-900">Offline Payment</h3>
                  <p className="text-sm text-slate-500">
                    Fill transaction details and submit for verification.
                  </p>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="h-10 w-10 rounded-xl border bg-white hover:bg-slate-100 grid place-items-center"
                >
                  <FiX />
                </button>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="p-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2">
                      <label className="text-xs font-bold text-slate-600">Method</label>
                      <select
                        value={otherPaymentDetails.method}
                        onChange={(e) =>
                          setOtherPaymentDetails((prev) => ({ ...prev, method: e.target.value }))
                        }
                        className="mt-1 h-11 w-full px-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Select Payment Method</option>
                        <option value="IMPS">IMPS</option>
                        <option value="NEFT">NEFT</option>
                        <option value="RTGS">RTGS</option>
                        <option value="DD">DD</option>
                        <option value="CHECK">Cheque</option>
                      </select>
                    </div>

                    <div>
                      <label className="text-xs font-bold text-slate-600">Amount</label>
                      <input
                        type="number"
                        placeholder="Amount"
                        value={otherPaymentDetails.amount}
                        onChange={(e) =>
                          setOtherPaymentDetails((prev) => ({ ...prev, amount: e.target.value }))
                        }
                        className="mt-1 h-11 w-full px-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="text-xs font-bold text-slate-600">Date</label>
                      <input
                        type="date"
                        value={otherPaymentDetails.date}
                        onChange={(e) =>
                          setOtherPaymentDetails((prev) => ({ ...prev, date: e.target.value }))
                        }
                        className="mt-1 h-11 w-full px-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label className="text-xs font-bold text-slate-600">Bank Name</label>
                      <input
                        type="text"
                        placeholder="Bank Name"
                        value={otherPaymentDetails.bankName}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^[a-zA-Z0-9\s]*$/.test(value)) {
                            setOtherPaymentDetails((prev) => ({ ...prev, bankName: value }));
                          }
                        }}
                        className="mt-1 h-11 w-full px-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="text-xs font-bold text-slate-600">Transaction ID</label>
                      <input
                        type="text"
                        placeholder="Transaction ID"
                        value={otherPaymentDetails.transactionId}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^[a-zA-Z0-9\s]*$/.test(value)) {
                            setOtherPaymentDetails((prev) => ({
                              ...prev,
                              transactionId: value,
                            }));
                          }
                        }}
                        className="mt-1 h-11 w-full px-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="text-xs font-bold text-slate-600">IFSC Code</label>
                      <input
                        type="text"
                        placeholder="IFSC Code"
                        value={otherPaymentDetails.ifscCode}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^[a-zA-Z0-9\s]*$/.test(value)) {
                            setOtherPaymentDetails((prev) => ({ ...prev, ifscCode: value }));
                          }
                        }}
                        className="mt-1 h-11 w-full px-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label className="text-xs font-bold text-slate-600">Account Number</label>
                      <input
                        type="text"
                        placeholder="Account Number"
                        value={otherPaymentDetails.accountNumber}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^[a-zA-Z0-9\s]*$/.test(value)) {
                            setOtherPaymentDetails((prev) => ({
                              ...prev,
                              accountNumber: value,
                            }));
                          }
                        }}
                        className="mt-1 h-11 w-full px-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="mt-5 flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={handleOtherPaymentSubmit}
                      className="h-11 w-full rounded-xl bg-blue-600 text-white font-extrabold hover:bg-blue-700"
                    >
                      Submit Payment
                    </button>
                    <button
                      onClick={() => setShowModal(false)}
                      className="h-11 w-full rounded-xl bg-slate-100 text-slate-800 font-extrabold hover:bg-slate-200 border"
                    >
                      Cancel
                    </button>
                  </div>

                  <p className="text-xs text-slate-500 mt-3">
                    Note: Please ensure transaction details are correct. Payments are verified by
                    the accounts team.
                  </p>
                </div>

                <div className="p-5 border-t lg:border-t-0 lg:border-l bg-white">
                  <div className="rounded-2xl border bg-slate-50 p-5">
                    <h4 className="text-lg font-extrabold text-slate-900">Company Bank Details</h4>
                    <p className="text-sm text-slate-500 mt-1">
                      Transfer the amount to the following account and submit details.
                    </p>

                    <div className="mt-4 space-y-2 text-sm text-slate-700">
                      <Row label="Company Name" value="OGCS Private Limited" />
                      <Row label="Account No" value="50200084105798" />
                      <Row label="Account Name" value="OGCS Private Limited" />
                      <Row label="Bank Name" value="HDFC Bank" />
                      <Row label="Branch" value="New Panvel" />
                      <Row label="IFSC Code" value="HDFC0000256" />
                      <Row label="Contact" value="+91 9967610135" />
                      <Row label="Email" value="support@ogcs.co.in" />
                    </div>

                    <div className="mt-5 rounded-xl bg-white border p-4">
                      <p className="text-xs font-bold text-slate-600">Selected Products</p>
                      <p className="text-sm text-slate-900 font-extrabold mt-1">
                        {selectedCount} product{selectedCount === 1 ? "" : "s"} | ₹
                        {Number(selectedTotal || 0).toFixed(2)}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        This amount should match your transfer amount.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-5 py-3 border-t bg-white flex items-center justify-between">
                <span className="text-xs text-slate-500">Secure offline payment submission</span>
                <button
                  onClick={() => setShowModal(false)}
                  className="text-sm font-bold text-slate-700 hover:text-slate-900"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <button
        onClick={handleGenerateInvoice}
        className="hidden sm:flex fixed bottom-6 right-6 bg-emerald-600 text-white px-5 py-3 rounded-full shadow-lg hover:bg-emerald-700 transition-all items-center gap-2 font-extrabold"
      >
        <FiFileText />
        Invoice
      </button>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4">
    <span className="text-slate-500 font-semibold">{label}</span>
    <span className="text-slate-900 font-bold text-right">{value}</span>
  </div>
);

export default SavedProducts;